import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

import ChevronDownIcon from "../../resources/route-builder/chevron-down.svg";
import SubtleButton from "../Buttons/SubtleButton";
import { useDismissiblePopover } from "./useDismissiblePopover";

export type DropdownSelectOption = {
  value: string;
  label: string;
};

export type DropdownSelectSize = "sm" | "md";

type DropdownSelectBase = {
  label: string;
  options: DropdownSelectOption[];
  testId?: string;
  className?: string;
  size?: DropdownSelectSize;
  icon?: React.ReactNode;
};

type SingleDropdownSelectProps = DropdownSelectBase & {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
};

type MultiDropdownSelectProps = DropdownSelectBase & {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
  emptyLabel: string;
};

export type DropdownSelectProps =
  | SingleDropdownSelectProps
  | MultiDropdownSelectProps;

const sizeClassNames: Record<
  DropdownSelectSize,
  { wrapper: string; control: string; chevron: string; option: string }
> = {
  sm: {
    wrapper: "mt-0.5",
    control: "rounded-md py-1 pl-2 pr-8 text-xs",
    chevron: "right-2",
    option: "px-2 py-1 text-xs",
  },
  md: {
    wrapper: "mt-1",
    control: "rounded-lg py-2 pl-3 pr-10 text-sm",
    chevron: "right-3",
    option: "px-3 py-1.5 text-sm",
  },
};

const controlClassName =
  "block w-full appearance-none border bg-white text-left text-slate-800";

export function multiSelectSummary(
  value: string[],
  options: DropdownSelectOption[],
  emptyLabel: string,
): string {
  if (value.length === 0) return emptyLabel;
  const firstLabel =
    options.find((option) => option.value === value[0])?.label ?? value[0];
  return `${firstLabel}${value.length > 1 ? ` +${value.length - 1}` : ""}`;
}

export default function DropdownSelect(props: DropdownSelectProps) {
  const sizeClassName = sizeClassNames[props.size ?? "sm"];
  const className = `${controlClassName} ${sizeClassName.control} ${props.className ?? "border-slate-300"}`;
  return (
    <div className={`relative min-w-0 ${sizeClassName.wrapper}`}>
      {props.multiple ? (
        <MultiDropdownSelect
          {...props}
          className={className}
          optionClassName={sizeClassName.option}
        />
      ) : (
        <SingleDropdownSelect
          {...props}
          className={className}
          optionClassName={sizeClassName.option}
        />
      )}
      <img
        src={ChevronDownIcon}
        alt=""
        aria-hidden
        className={`pointer-events-none absolute ${sizeClassName.chevron} top-1/2 h-4 w-4 -translate-y-1/2`}
      />
    </div>
  );
}

const menuClassName =
  "z-40 max-h-48 rounded-md border border-slate-200 bg-white py-1 shadow-lg";

/** Open state plus a fixed-position menu that flips above the trigger when space is short. */
function useDropdownMenu(minWidth = 0) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const close = useCallback(() => setOpen(false), []);
  useDismissiblePopover(open, rootRef, close);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
      const available = (openUp ? spaceAbove : spaceBelow) - 8;
      const width = Math.min(
        Math.max(rect.width, minWidth),
        window.innerWidth - 16,
      );
      setMenuStyle({
        position: "fixed",
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        width,
        maxHeight: Math.max(96, Math.min(192, available)),
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, minWidth]);

  return { open, setOpen, close, rootRef, triggerRef, menuStyle };
}

function SingleDropdownSelect({
  label,
  options,
  value,
  onChange,
  testId,
  className,
  optionClassName,
  icon,
}: SingleDropdownSelectProps & { className: string; optionClassName: string }) {
  const { open, setOpen, close, rootRef, triggerRef, menuStyle } =
    useDropdownMenu(160);
  const selectedLabel =
    options.find((option) => option.value === value)?.label ?? value;

  const select = (optionValue: string) => {
    onChange(optionValue);
    close();
    triggerRef.current?.focus();
  };

  return (
    <div ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="listbox"
        data-testid={testId}
        className={className}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="flex items-center gap-1.5">
          {icon}
          <span className="block truncate">{selectedLabel}</span>
        </span>
      </button>
      {open ? (
        <ul
          role="listbox"
          aria-label={label}
          style={menuStyle}
          className={`${menuClassName} overflow-y-auto`}
        >
          {options.map((option) => {
            const selected = option.value === value;
            return (
              <li
                key={option.value || option.label}
                role="option"
                aria-selected={selected}
              >
                <button
                  type="button"
                  className={`block w-full truncate text-left text-slate-800 hover:bg-slate-50 ${
                    selected ? "bg-slate-100 font-medium" : ""
                  } ${optionClassName}`}
                  data-testid={
                    testId ? `${testId}-option-${option.value}` : undefined
                  }
                  onClick={() => select(option.value)}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function MultiDropdownSelect({
  label,
  options,
  value,
  onChange,
  emptyLabel,
  testId,
  className,
  optionClassName,
}: MultiDropdownSelectProps & { className: string; optionClassName: string }) {
  const { open, setOpen, rootRef, triggerRef, menuStyle } =
    useDropdownMenu(224);
  const summary = multiSelectSummary(value, options, emptyLabel);

  const toggle = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((current) => current !== optionValue)
        : [...value, optionValue],
    );
  };

  return (
    <div ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="listbox"
        data-testid={testId}
        className={className}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="block truncate">{summary}</span>
      </button>
      {open ? (
        <div
          style={menuStyle}
          className={`${menuClassName} flex flex-col overflow-hidden`}
        >
          <ul
            className="min-h-0 overflow-y-auto"
            role="listbox"
            aria-multiselectable="true"
            aria-label={label}
          >
            {options.map((option) => {
              const checked = value.includes(option.value);
              return (
                <li key={option.value} role="option" aria-selected={checked}>
                  <label
                    className={`flex cursor-pointer items-center gap-2 text-slate-800 hover:bg-slate-50 ${optionClassName}`}
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 shrink-0 accent-snow-blue-3"
                      checked={checked}
                      data-testid={
                        testId ? `${testId}-option-${option.value}` : undefined
                      }
                      onChange={() => toggle(option.value)}
                    />
                    <span className="min-w-0 truncate">{option.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
          <div className="flex shrink-0 items-center justify-end border-t border-slate-200 bg-white px-2 pt-1">
            <SubtleButton
              label="Clear"
              className="!px-2 !py-1 !text-xs"
              onClick={() => onChange([])}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
